// DeluxeRoom.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Thumbs, FreeMode } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/thumbs';
import 'swiper/css/free-mode';

import {
    BedDouble, ShowerHead, Wifi, Coffee, Wind, Droplets, Leaf, Sun, Trees,
    ShieldCheck, Sparkles, Phone, ChevronRight
} from 'lucide-react';

// Images (replace paths with your actual assets)
import heroImg from '../assets/Mahi/76.jpeg';
import gallery1 from '../assets/Mahi/81.jpeg';
import gallery2 from '../assets/Mahi/84.jpeg';
import gallery3 from '../assets/Mahi/83.jpeg';
import gallery4 from '../assets/Mahi/86.jpeg';
import gallery5 from '../assets/Mahi/42.jpeg';
import gallery6 from '../assets/Mahi/37.jpeg';

const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 1, ease: "easeOut" } }
};

const staggerContainer = {
    hidden: { opacity: 1 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.18, delayChildren: 0.25 }
    }
};

const childFade = {
    hidden: { opacity: 0, y: 25 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9 } }
};

export default function DeluxeRoom() {
    const { t } = useTranslation('rooms');
    const [thumbsSwiper, setThumbsSwiper] = useState(null);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const galleryImages = [gallery1, gallery2, gallery3, gallery4, gallery5, gallery6];

    const highlights = [
        { icon: Sun, title: t('deluxeRoom.highlight1Title'), desc: t('deluxeRoom.highlight1Desc') },
        { icon: BedDouble, title: t('deluxeRoom.highlight2Title'), desc: t('deluxeRoom.highlight2Desc') },
        { icon: ShowerHead, title: t('deluxeRoom.highlight3Title'), desc: t('deluxeRoom.highlight3Desc') },
    ];

    const inclusions = [
        t('deluxeRoom.inclusion1'),
        t('deluxeRoom.inclusion2'),
        t('deluxeRoom.inclusion3'),
        t('deluxeRoom.inclusion4'),
        t('deluxeRoom.inclusion5'),
        t('deluxeRoom.inclusion6'),
    ];

    const stats = [
        { icon: Sparkles, label: t('deluxeRoom.statSize'), value: "45 m²" },
        { icon: Sun, label: t('deluxeRoom.statView'), value: t('deluxeRoom.statViewValue') },
        { icon: Trees, label: t('deluxeRoom.statGuests'), value: "2 adults" },
        { icon: BedDouble, label: t('deluxeRoom.statBed'), value: "King size" },
    ];

    return (
        <div className="bg-[#F8F5ED] text-[#1A2F2A] min-h-screen font-sans">
            {/* ─── HERO ──────────────────────────────────────────────── */}
            <section className="relative h-[70vh] min-h-[420px] sm:h-[75vh] sm:min-h-[520px] flex items-center sm:items-end justify-center overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src={heroImg}
                        alt="Deluxe Room hero"
                        className="w-full h-full object-cover brightness-[0.78] scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/60" />
                </div>

                <div className="relative z-10 container px-5 sm:px-8 pb-10 sm:pb-16 md:pb-24 max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.2 }}
                        className="max-w-3xl"
                    >
                        <span className="inline-block text-[#E8C07D] uppercase tracking-widest text-xs sm:text-sm md:text-base font-medium mb-4 sm:mb-5">
                            {t('deluxeRoom.resortName')}
                        </span>

                        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-serif font-light text-white leading-tight drop-shadow-2xl">
                            {t('deluxeRoom.heroTitle')}
                        </h1>

                        <p className="mt-5 sm:mt-7 text-lg sm:text-xl md:text-2xl lg:text-2xl xl:text-3xl font-light text-[#E8C07D] tracking-wide">
                            {t('deluxeRoom.heroSubtitle')}
                        </p>

                        <div className="mt-8 sm:mt-12 flex flex-wrap gap-4 sm:gap-5">
                            <Link
                                to="/search"
                                className="bg-[#1A2F2A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#E8C07D] hover:text-[#1A2F2A] transition-all shadow-lg flex items-center gap-2 group"
                            >
                                <span>{t('deluxeRoom.bookThisRoom')}</span>
                                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </Link>

                            <a
                                href="https://wa.me/+94766111678"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="border-2 border-white/70 text-white px-8 py-4 rounded-full font-medium hover:bg-white/10 transition-all flex items-center gap-2"
                            >
                                <Phone className="w-5 h-5" />
                                {t('deluxeRoom.callReservations')}
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ─── HIGHLIGHTS + STATS ────────────────────────────────── */}
            <section className="py-20 md:py-28 lg:py-36 bg-gradient-to-b from-[#F8F5ED] to-[#F2EEE5]">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                        className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start"
                    >
                        {/* Left - Highlights */}
                        <motion.div variants={fadeInUp} className="space-y-10">
                            <div>
                                <span className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                                    {t('deluxeRoom.signatureFeatures')}
                                </span>
                                <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                                    {t('deluxeRoom.deluxeRoomName')}
                                </h2>
                            </div>

                            <div className="space-y-7">
                                {highlights.map((item, i) => (
                                    <motion.div
                                        key={i}
                                        variants={childFade}
                                        className="flex gap-5 p-6 rounded-2xl bg-white/70 backdrop-blur-sm border border-[#E8C07D]/10 hover:border-[#E8C07D]/40 hover:shadow-xl transition-all group"
                                    >
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#E8C07D]/10 flex items-center justify-center group-hover:bg-[#E8C07D] transition-colors">
                                            <item.icon className="w-6 h-6 text-[#E8C07D] group-hover:text-white transition-colors" />
                                        </div>
                                        <div>
                                            <h4 className="text-xl font-serif font-light text-[#1A2F2A] mb-2">{item.title}</h4>
                                            <p className="text-[#2D3F3A]/90 leading-relaxed">{item.desc}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Right - At a Glance */}
                        <motion.div variants={fadeInUp} className="bg-white/80 backdrop-blur-md rounded-3xl p-8 md:p-10 shadow-xl border border-[#E8C07D]/10">
                            <h3 className="text-2xl md:text-3xl font-serif font-light text-[#1A2F2A] mb-8">
                                {t('deluxeRoom.atAGlance')}
                            </h3>

                            <div className="grid grid-cols-2 gap-6">
                                {stats.map((stat, i) => (
                                    <div key={i} className="text-center">
                                        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#E8C07D]/10 flex items-center justify-center">
                                            <stat.icon className="w-7 h-7 text-[#E8C07D]" />
                                        </div>
                                        <p className="text-sm text-[#2D3F3A]/80">{stat.label}</p>
                                        <p className="text-xl md:text-2xl font-light text-[#1A2F2A] mt-1">{stat.value}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-10 pt-8 border-t border-[#E8C07D]/20">
                                <Link
                                    to="/search"
                                    className="w-full block bg-[#1A2F2A] text-white text-center py-4 rounded-full font-medium hover:bg-[#E8C07D] hover:text-[#1A2F2A] transition-all shadow-lg"
                                >
                                    {t('deluxeRoom.reserveThisRoom')}
                                </Link>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* ─── GALLERY ───────────────────────────────────────────── */}
            <section className="py-20 md:py-28 bg-[#F2EEE5]">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                        className="text-center mb-12 md:mb-16"
                    >
                        <motion.h2 variants={childFade} className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                            {t('deluxeRoom.discoverTheSpace')}
                        </motion.h2>
                    </motion.div>

                    <Swiper
                        modules={[Thumbs, FreeMode, Navigation, Pagination]}
                        spaceBetween={12}
                        navigation
                        pagination={{ clickable: true }}
                        thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                        loop={true}
                        className="main-gallery rounded-3xl overflow-hidden shadow-2xl mb-6"
                    >
                        {galleryImages.map((img, idx) => (
                            <SwiperSlide key={idx}>
                                <div className="aspect-[4/3] md:aspect-[16/9] relative">
                                    <img
                                        src={img}
                                        alt={`Deluxe Room view ${idx + 1}`}
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>

                    <Swiper
                        onSwiper={setThumbsSwiper}
                        modules={[FreeMode, Thumbs]}
                        spaceBetween={10}
                        slidesPerView={4}
                        freeMode={true}
                        watchSlidesProgress={true}
                        className="thumbs-gallery"
                        breakpoints={{
                            640: { slidesPerView: 5 },
                            1024: { slidesPerView: 6 },
                        }}
                    >
                        {galleryImages.map((img, idx) => (
                            <SwiperSlide key={idx}>
                                <div className="aspect-[4/3] rounded-xl overflow-hidden opacity-75 hover:opacity-100 transition-opacity cursor-pointer border-2 border-transparent hover:border-[#E8C07D]">
                                    <img
                                        src={img}
                                        alt={`Thumbnail ${idx + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </section>

            {/* ─── INCLUSIONS & AMENITIES ────────────────────────────── */}
            <section className="py-20 md:py-28 bg-gradient-to-b from-[#F2EEE5] to-[#F8F5ED]">
                <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-7xl">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={staggerContainer}
                        className="text-center mb-16"
                    >
                        <motion.span variants={childFade} className="text-[#E8C07D] uppercase tracking-widest text-sm md:text-base font-medium">
                            {t('deluxeRoom.whatsIncluded')}
                        </motion.span>
                        <motion.h2 variants={childFade} className="mt-4 text-4xl md:text-5xl lg:text-6xl font-serif font-light text-[#1A2F2A]">
                            {t('deluxeRoom.deluxeComfortCare')}
                        </motion.h2>
                    </motion.div>

                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
                        {/* Inclusions list */}
                        <motion.div variants={fadeInUp} className="space-y-6">
                            <div className="flex items-center gap-4 mb-8">
                                <ShieldCheck className="w-10 h-10 text-[#E8C07D]" />
                                <h3 className="text-3xl font-serif font-light text-[#1A2F2A]">
                                    {t('deluxeRoom.inclusions')}
                                </h3>
                            </div>

                            <div className="space-y-4">
                                {inclusions.map((item, i) => (
                                    <motion.div
                                        key={i}
                                        variants={childFade}
                                        className="flex gap-4 p-5 rounded-2xl bg-white/70 backdrop-blur-sm border border-[#E8C07D]/10 hover:border-[#E8C07D]/40 transition-all"
                                    >
                                        <Sparkles className="w-6 h-6 text-[#E8C07D] flex-shrink-0 mt-1" />
                                        <p className="text-lg leading-relaxed text-[#2D3F3A]/90">{item}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Core amenities icons */}
                        <motion.div variants={fadeInUp} className="space-y-10">
                            <h3 className="text-3xl font-serif font-light text-[#1A2F2A] text-center lg:text-left">
                                {t('deluxeRoom.coreAmenities')}
                            </h3>

                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                                {[
                                    { icon: Wifi, label: t('deluxeRoom.amenityHighSpeedWifi') },
                                    { icon: Coffee, label: t('deluxeRoom.amenityHerbalTeaCoffee') },
                                    { icon: Wind, label: t('deluxeRoom.amenityIndividuallyControlledAC') },
                                    { icon: ShowerHead, label: t('deluxeRoom.amenityRainShower') },
                                    { icon: Droplets, label: t('deluxeRoom.amenityOrganicToiletries') },
                                    { icon: Leaf, label: t('deluxeRoom.amenityDailyFreshFlowers') },
                                ].map((item, i) => (
                                    <div key={i} className="text-center">
                                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E8C07D]/10 flex items-center justify-center">
                                            <item.icon className="w-8 h-8 text-[#E8C07D]" />
                                        </div>
                                        <p className="text-sm md:text-base font-light text-[#2D3F3A]">{item.label}</p>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ─── FINAL CTA ─────────────────────────────────────────── */}
            <section className="relative py-24 md:py-32 overflow-hidden bg-[#1A2F2A]">
                <div className="absolute inset-0 opacity-15 bg-[radial-gradient(circle_at_30%_70%,#E8C07D_0%,transparent_60%)]" />

                <div className="relative container px-5 sm:px-8 max-w-5xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2 }}
                    >
                        <Leaf className="w-14 h-14 text-[#E8C07D] mx-auto mb-8" />

                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-white mb-6">
                            {t('deluxeRoom.beginYourHealingStay')}
                        </h2>

                        <p className="text-xl md:text-2xl text-[#E8C07D]/90 mb-12 max-w-3xl mx-auto font-light">
                            {t('deluxeRoom.experienceAyurvedicSerenity')}
                        </p>

                        <div className="flex flex-col sm:flex-row gap-6 justify-center">
                            <Link
                                to="/search"
                                className="bg-[#E8C07D] text-[#1A2F2A] px-10 py-5 rounded-full font-medium text-lg hover:bg-white transition-all shadow-xl"
                            >
                                {t('deluxeRoom.checkAvailabilityBook')}
                            </Link>

                            <a
                                href="https://wa.me/+94766111678"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="border-2 border-[#E8C07D] text-[#E8C07D] px-10 py-5 rounded-full font-medium text-lg hover:bg-[#E8C07D]/10 transition-all flex items-center justify-center gap-2"
                            >
                                <Phone className="w-5 h-5" />
                                {t('deluxeRoom.speakWithReservations')}
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}